import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useGetHostPotentialPayoutQuery } from "../../services/contracts.service";
import { useGetPausedInternallyListingsQuery } from "../../services/listings.service";
import {
  useGetUnreadMessageCountQuery,
  useSearchMessagesQuery,
} from "../../services/messages.service";
import { useGetHostResponsivenessQuery } from "../../services/profiles.service";
import {
  toBookingRequestsAndInquiriesCard,
  toReviewBookingUpdateCards,
  toUnreadMessagesCard,
} from "../services/api/todo/converter";
import {
  BOOKING_REQUESTS_AND_INQUIRIES_PAGE_SIZE,
  REVIEW_BOOKING_UPDATES_PAGE_SIZE,
} from "../services/api/todo/converter.consts";
import type { UseHostHubDataResult } from "../types/host-hub-data.types";
import { toPausedListingItems } from "../utils/host-hub-paused-listing.utils";

const useHostHubData = (): UseHostHubDataResult => {
  const ssoId = useSelector(selectProfileSSOid);

  const search = useSearchMessagesQuery(
    ssoId
      ? {
          filters: ["requests", "inquiries"],
          participant_ssoid: ssoId,
          size: BOOKING_REQUESTS_AND_INQUIRIES_PAGE_SIZE,
        }
      : skipToken,
  );

  const updateRequests = useSearchMessagesQuery(
    ssoId
      ? {
          booking_state: ["GUEST_UPDATE_REQUESTED"],
          participant_ssoid: ssoId,
          size: REVIEW_BOOKING_UPDATES_PAGE_SIZE,
        }
      : skipToken,
  );

  const unread = useGetUnreadMessageCountQuery();

  const potentialPayout = useGetHostPotentialPayoutQuery(undefined, {
    skip: !ssoId,
  });

  const responsiveness = useGetHostResponsivenessQuery();

  const pausedInternally = useGetPausedInternallyListingsQuery(
    ssoId ? { ssoId } : skipToken,
  );

  const bookingRequestsAndInquiries = {
    card: toBookingRequestsAndInquiriesCard(
      search.data,
      ssoId,
      potentialPayout.data?.potential_payout,
      potentialPayout.data?.currency,
    ),
    isLoading: search.isLoading || potentialPayout.isLoading,
    isError: search.isError || potentialPayout.isError,
  };
  const reviewBookingUpdates = {
    cards: toReviewBookingUpdateCards(updateRequests.data, ssoId),
    isLoading: updateRequests.isLoading,
    isError: updateRequests.isError,
  };
  const unreadMessages = {
    card: toUnreadMessagesCard(unread.data),
    data: responsiveness.data
      ? { hours: responsiveness.data.hours }
      : undefined,
    isLoading: unread.isLoading || responsiveness.isLoading,
    isError: unread.isError || responsiveness.isError,
  };
  const pausedListingItems = toPausedListingItems(
    pausedInternally.data?.listings,
  );
  const pausedListings = {
    items: pausedListingItems,
    isEligible: pausedListingItems.length > 0,
    isLoading: pausedInternally.isLoading,
    isError: pausedInternally.isError,
  };

  const hasAnyTodo =
    pausedListings.isEligible ||
    bookingRequestsAndInquiries.card.isEligible ||
    reviewBookingUpdates.cards.some(({ isEligible }) => isEligible) ||
    unreadMessages.card.isEligible;
  const isLoading =
    pausedListings.isLoading ||
    bookingRequestsAndInquiries.isLoading ||
    reviewBookingUpdates.isLoading ||
    unreadMessages.isLoading;

  return {
    bookingRequestsAndInquiries,
    hasAnyTodo,
    isLoading,
    pausedListings,
    reviewBookingUpdates,
    unreadMessages,
  };
};

export default useHostHubData;
